import type { Option } from '@branchjs/neo-components';
import { createSelector } from 'reselect';

import type { RootState } from 'client/js/redux';
import { COST_FILE_TEMPLATE_CREATOR_STEPS } from './constants';
import { OMIT_FIELD } from './constants/costFileTemplateBranchFieldOptions';
import { INIT_STATE } from './constants/costFileTemplateCreatorInitState';
import { BranchFieldOptionService } from './services/BranchFieldOptionService';
import { CostFileTemplateAdapterService } from './services/CostFileTemplateAdapterService';
import type { CostFileTemplateDto } from './services/CostFileTemplateAdapterService';
import type { CostFileTemplateCreatorState, CostFileTemplateCreatorStep, CostFileTemplateField } from './types';

export const selectCostFileTemplateCreatorState = (state: RootState): CostFileTemplateCreatorState => {
  return state.costFileTemplateCreator || INIT_STATE;
};

export const selectEditingCostFileTemplateId = createSelector<
  RootState,
  CostFileTemplateCreatorState,
  CostFileTemplateCreatorState['id']
>(selectCostFileTemplateCreatorState, (costFileTemplateCreator) => {
  return costFileTemplateCreator.id;
});

export const selectIsEditing = createSelector<RootState, CostFileTemplateCreatorState, boolean>(
  selectCostFileTemplateCreatorState,
  (costFileTemplateCreator) => {
    return !!costFileTemplateCreator.id;
  }
);

export const selectCostFileTemplateCreatorStep = createSelector<
  RootState,
  CostFileTemplateCreatorState,
  CostFileTemplateCreatorState['title']
>(selectCostFileTemplateCreatorState, (costFileTemplateCreator) => {
  return costFileTemplateCreator.step;
});

export const selectHasVisitedCostFileTemplateStep: (state: RootState, step: CostFileTemplateCreatorStep) => boolean =
  createSelector(
    selectCostFileTemplateCreatorState,
    (_state, step: CostFileTemplateCreatorStep) => step,
    (state, step) => {
      switch (step) {
        case COST_FILE_TEMPLATE_CREATOR_STEPS.START: {
          return state.startStepVisited;
        }
        case COST_FILE_TEMPLATE_CREATOR_STEPS.MAP: {
          return state.mapStepVisited;
        }
        case COST_FILE_TEMPLATE_CREATOR_STEPS.VALIDATE: {
          return state.validateStepVisited;
        }
      }
    }
  );

export const selectCostFileTemplateTitle = createSelector<
  RootState,
  CostFileTemplateCreatorState,
  CostFileTemplateCreatorState['title']
>(selectCostFileTemplateCreatorState, (costFileTemplateCreator) => {
  return costFileTemplateCreator.title;
});

export const selectCostFileTemplateAdPartner = createSelector<
  RootState,
  CostFileTemplateCreatorState,
  CostFileTemplateCreatorState['adPartner']
>(selectCostFileTemplateCreatorState, (costFileTemplateCreator) => {
  return costFileTemplateCreator.adPartner;
});

export const selectCostFileTemplateSampleFile = createSelector<
  RootState,
  CostFileTemplateCreatorState,
  CostFileTemplateCreatorState['sampleFile']['uploadedFile']
>(selectCostFileTemplateCreatorState, (costFileTemplateCreator) => {
  return costFileTemplateCreator.sampleFile.uploadedFile;
});

export const selectCostFileTemplateMapping = createSelector<
  RootState,
  CostFileTemplateCreatorState,
  CostFileTemplateCreatorState['mapping']
>(selectCostFileTemplateCreatorState, (costFileTemplateCreator) => {
  return costFileTemplateCreator.mapping;
});

export const selectCostFileTemplateMappingFields = createSelector<
  RootState,
  CostFileTemplateCreatorState['mapping'],
  CostFileTemplateDto['mapping']
>(selectCostFileTemplateMapping, (mapping) => {
  const adapter = new CostFileTemplateAdapterService();
  return adapter.stateMappingToApiPayload(mapping);
});

export const selectCostFileTemplateFieldCount = createSelector<RootState, CostFileTemplateCreatorState['mapping'], number>(
  selectCostFileTemplateMapping,
  (mapping) => {
    return mapping.length;
  }
);

export const selectCostFileTemplateField: (state: RootState, fieldIndex: number) => CostFileTemplateField | undefined =
  createSelector(
    selectCostFileTemplateMapping,
    (_state, fieldIndex: number) => fieldIndex,
    (mapping, fieldIndex) => {
      return mapping[fieldIndex] || undefined;
    }
  );

const selectBranchFieldOptionService = createSelector<
  RootState,
  CostFileTemplateCreatorState['mapping'],
  BranchFieldOptionService
>(selectCostFileTemplateMapping, (mapping) => {
  return new BranchFieldOptionService(mapping);
});

export const selectCostFileTemplateFieldOptions: (state: RootState, fieldIndex: number) => any = createSelector(
  selectBranchFieldOptionService,
  (_state, fieldIndex: number) => fieldIndex,
  (service, fieldIndex) => {
    const requiredOptions = service.getRequiredOptions(fieldIndex);
    const optionalOptions = service.getOptionalOptions(fieldIndex);

    return [
      { ...OMIT_FIELD },
      {
        label: 'Required',
        options: requiredOptions,
      },
      {
        label: 'Optional',
        options: optionalOptions,
      },
    ];
  }
);

export const selectCostFileTemplateStartCompleted = createSelector<RootState, string, Option | undefined, boolean>(
  selectCostFileTemplateTitle,
  selectCostFileTemplateAdPartner,
  (title, adPartner) => {
    const stepCompleted = !!title && !!adPartner?.value;

    return stepCompleted;
  }
);

export const selectCostFileTemplateMappingCompleted = createSelector<RootState, BranchFieldOptionService, boolean>(
  selectBranchFieldOptionService,
  (service) => {
    return service.getRequiredOptions().every((option) => option.isDisabled);
  }
);

export const selectHasCompletedCostFileTemplateStep: (state: RootState, step: CostFileTemplateCreatorStep) => boolean =
  createSelector(
    selectCostFileTemplateStartCompleted,
    selectCostFileTemplateMappingCompleted,
    (_state, step: CostFileTemplateCreatorStep) => step,
    (startCompleted, mappingCompleted, step) => {
      switch (step) {
        case COST_FILE_TEMPLATE_CREATOR_STEPS.START: {
          return startCompleted;
        }
        case COST_FILE_TEMPLATE_CREATOR_STEPS.MAP: {
          return mappingCompleted;
        }
        case COST_FILE_TEMPLATE_CREATOR_STEPS.VALIDATE: {
          return startCompleted && mappingCompleted;
        }
      }
    }
  );

export const selectCostFileTemplateCurrentStepIsCompleted = createSelector<RootState, string, boolean, boolean, boolean>(
  selectCostFileTemplateCreatorStep,
  selectCostFileTemplateStartCompleted,
  selectCostFileTemplateMappingCompleted,
  (step, startCompleted, mappingCompleted) => {
    switch (step) {
      case COST_FILE_TEMPLATE_CREATOR_STEPS.START: {
        return startCompleted;
      }
      case COST_FILE_TEMPLATE_CREATOR_STEPS.MAP: {
        return mappingCompleted;
      }
      case COST_FILE_TEMPLATE_CREATOR_STEPS.VALIDATE: {
        return startCompleted && mappingCompleted;
      }
    }

    return false;
  }
);

export const selectCostFileTemplateCreatorAllStepsCompleted = createSelector<RootState, boolean, boolean, boolean>(
  selectCostFileTemplateStartCompleted,
  selectCostFileTemplateMappingCompleted,
  (startCompleted, mappingCompleted) => {
    return startCompleted && mappingCompleted;
  }
);
